import React, { useState } from "react";
import { api } from "@/lib/api";
import { usePaywallGuard } from "@/lib/usePaywallGuard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import PlaceInput from "@/components/PlaceInput";
import { CalendarCheck, Loader2, Sparkles } from "lucide-react";

const EVENTS = [
  { value: "marriage", label: "💍 Marriage" },
  { value: "griha_pravesh", label: "🏠 Griha Pravesh (House Warming)" },
  { value: "business_launch", label: "📈 Business Launch" },
  { value: "vehicle_purchase", label: "🚗 Vehicle Purchase" },
  { value: "travel", label: "✈ First Journey / Travel" },
  { value: "general", label: "✨ General Auspicious Day" },
];

const VERDICT_FILTERS = [
  { value: "Excellent", label: "Only Excellent" },
  { value: "Good", label: "Good & above" },
  { value: "Average", label: "Average & above" },
];

/**
 * Muhurat Finder — pick auspicious slots in the next N days for a chosen
 * event, optionally filtered by the user's natal Chandra Bala. Natal data is
 * optional — users can get a generic list without supplying it.
 */
export default function Muhurat() {
  const guard = usePaywallGuard();
  const [event, setEvent] = useState("marriage");
  const [daysAhead, setDaysAhead] = useState(45);
  const [minVerdict, setMinVerdict] = useState("Good");
  const [form, setForm] = useState({ dob: "", time: "12:00", name: "" });
  const [city, setCity] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async (e) => {
    e.preventDefault();
    setError("");
    if (!city) { setError("Please select a city (so we can check your Chandra Bala)"); return; }
    setLoading(true);
    try {
      const body = {
        dob: form.dob || "2000-01-01",   // dummy natal if user didn't fill it
        time: form.time || "12:00",
        lat: city.lat,
        lon: city.lon,
        tz: city.tz,
        name: form.name || null,
        extra: { event, days_ahead: daysAhead, min_verdict: minVerdict },
      };
      const { data: res } = await guard(
        async () => await api.post("/astro/muhurat", body),
        "Muhurat Finder",
      );
      setData(res);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to find muhurats");
    } finally {
      setLoading(false);
    }
  };

  const verdictClass = (v) => v === "Excellent" ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/40"
    : v === "Good" ? "bg-amber-500/15 text-amber-300 border-amber-400/40"
    : v === "Average" ? "bg-slate-500/15 text-slate-300 border-slate-400/40"
    : "bg-rose-500/15 text-rose-300 border-rose-400/40";

  return (
    <div className="space-y-6" data-testid="muhurat-page">
      <header>
        <h1 className="font-serif text-4xl text-slate-50 mb-2">Muhurat Finder</h1>
        <p className="text-slate-400">Find auspicious date-time slots filtered by panchang & your natal Chandra Bala.</p>
      </header>

      <Card className="glass border-white/10 p-6">
        <form onSubmit={run} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="muhurat-form">
          <div>
            <Label className="text-slate-300">Event</Label>
            <Select value={event} onValueChange={setEvent}>
              <SelectTrigger data-testid="muhurat-event" className="bg-slate-900/50 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENTS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Window (days)</Label>
            <Input type="number" min="7" max="90" value={daysAhead} onChange={(e) => setDaysAhead(parseInt(e.target.value) || 30)} data-testid="muhurat-days" className="bg-slate-900/50 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-slate-300">Quality filter</Label>
            <Select value={minVerdict} onValueChange={setMinVerdict}>
              <SelectTrigger data-testid="muhurat-min-verdict" className="bg-slate-900/50 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VERDICT_FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">City</Label>
            <PlaceInput value={city} onChange={setCity} data-testid="muhurat-place" />
          </div>
          <div>
            <Label className="text-slate-300">Your DOB <span className="text-slate-500 text-xs">(optional, for Chandra Bala)</span></Label>
            <DatePicker value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} />
          </div>
          <div>
            <Label className="text-slate-300">Birth Time <span className="text-slate-500 text-xs">(optional)</span></Label>
            <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="muhurat-time" className="bg-slate-900/50 border-white/10 text-white" />
          </div>
          <div className="md:col-span-2 lg:col-span-4">
            <Button type="submit" disabled={loading} data-testid="muhurat-submit" className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium px-8">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching the panchang...</> : <>Find Auspicious Slots <CalendarCheck className="w-4 h-4 ml-2" /></>}
            </Button>
            {error && <span className="ml-3 text-sm text-rose-400" data-testid="muhurat-error">{error}</span>}
          </div>
        </form>
      </Card>

      {data && (
        <Card className="glass border-white/10 p-6" data-testid="muhurat-results">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h3 className="font-serif text-2xl text-slate-50 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-gold" /> {data.total_found} auspicious slot{data.total_found !== 1 ? "s" : ""}
            </h3>
            <span className="text-xs text-slate-500">Event: {data.event.replace("_", " ")} · Next {data.window_days} days</span>
          </div>
          {data.total_found === 0 ? (
            <div className="text-center py-10 text-slate-400">
              No slots match this quality filter. Try widening the window or lowering the filter.
            </div>
          ) : (
            <div className="space-y-3">
              {data.candidates.map((c) => (
                <div key={c.date} className="p-4 rounded-xl bg-slate-900/50 border border-white/5" data-testid={`muhurat-slot-${c.date}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="font-serif text-xl text-slate-50">{c.date}</div>
                      <div className="text-sm text-amber-300">{c.weekday}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2.5 py-1 rounded-full border ${verdictClass(c.verdict)}`}>{c.verdict}</span>
                      <span className="text-xs text-slate-500">score {c.score}/100</span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
                    <span><span className="text-slate-500">Tithi:</span> {c.panchang.tithi}</span>
                    <span><span className="text-slate-500">Nakshatra:</span> {c.panchang.nakshatra}</span>
                    <span><span className="text-slate-500">Yoga:</span> {c.panchang.yoga}</span>
                    {c.panchang.sunrise && <span><span className="text-slate-500">Sunrise:</span> {c.panchang.sunrise}</span>}
                  </div>
                  {c.reasons && c.reasons.length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {c.reasons.map((r, i) => (
                        <li key={i} className="text-xs text-slate-300">{r}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
